import PropTypes from 'prop-types'
import { withProps } from 'recompose'

import styled from '@emotion/styled'

import { noneOf } from './none-of'

const propMappers = {
  alignItems(props) {
    if (props.center) {
      return 'center'
    }

    return props.alignItems
  },
  flexDirection(props) {
    if (props.column) {
      return 'column'
    }

    return props.flexDirection
  },
  flexWrap(props) {
    if (props.wrap) {
      return 'wrap'
    }

    return props.flexWrap
  },
  justifyContent(props) {
    if (props.center) {
      return 'center'
    }

    return props.justifyContent
  },
}

/**
 * Render children in a flexbox wrapper
 * @param {Object} props
 * @param {string} props.alignItems Control CSS `align-items` value
 * @param {boolean} props.center Center children on both axis
 * @param {boolean} props.column Set `flex-direction` to column
 * @param {string} props.flexDirection Control CSS `flex-direction` value
 * @param {string} props.flexWrap Control CSS `flex-wrap` value
 * @param {boolean} props.inline Render container as inline element
 * @param {string} props.justifyContent Control CSS `justify-content` value
 * @param {boolean} props.wrap Set `flex-wrap` to `wrap`
 * @param {boolean} props.wide Set `width` to `100%`
 * @param {(number|string)} props.flexBasis Control CSS `flex-basis` value
 * @param {(number|string)} props.grow Control CSS `flex-grow` property
 * @param {(number|string)} props.shrink Control CSS `flex-shrink` property
 * @return {ReactComponent}
 * @example
 * <Flex center wide>
 *   <Button>Register</Button>
 * </Flex>
 */
export const Flex = withProps(props => ({
  alignItems: propMappers.alignItems(props),
  flexDirection: propMappers.flexDirection(props),
  flexWrap: propMappers.flexWrap(props),
  justifyContent: propMappers.justifyContent(props),
}))(styled('div', {
  shouldForwardProp: noneOf([
    'as',
    'alignItems',
    'column',
    'flexDirection',
    'flexWrap',
    'flexBasis',
    'grow',
    'shrink',
    'justifyContent',
    'inline',
    'wide',
    'wrap',
  ]),
})`
  display: ${props => (props.inline ? 'inline-flex' : 'flex')};
  flex-direction: ${props => props.flexDirection};
  flex-wrap: ${props => props.flexWrap};
  align-items: ${props => props.alignItems};
  justify-content: ${props => props.justifyContent};
  ${props => !props.inline && props.wide && 'width: 100%;'};
  flex-basis: ${props => props.flexBasis};
  flex-grow: ${props => props.grow};
  flex-shrink: ${props => props.shrink};
`)

Flex.propTypes = {
  alignItems: PropTypes.oneOf([
    'flex-start',
    'center',
    'flex-end',
    'stretch',
    'baseline',
  ]),
  flexBasis: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  grow: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  shrink: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  children: PropTypes.node,
  column: PropTypes.bool,
  flexDirection: PropTypes.oneOf([
    'column',
    'column-reverse',
    'row',
    'row-reverse',
  ]),
  flexWrap: PropTypes.oneOf(['nowrap', 'wrap', 'wrap-reverse']),
  justifyContent: PropTypes.oneOf([
    'flex-start',
    'center',
    'flex-end',
    'space-between',
    'space-around',
  ]),
  inline: PropTypes.bool,
  wide: PropTypes.bool,
  wrap: PropTypes.bool,
}

/**
 * Control flexbox children rendering
 * @name FlexItem
 * @param {Object} props
 * @param {string} props.basis Control CSS `flex-basis` property
 * @param {(number|string)} props.grow Control CSS `flex-grow` property
 * @param {(number|string)} props.order Control CSS `order` property
 * @param {(number|string)} props.shrink Control CSS `flex-shrink` property
 * @return {ReactComponent}
 * @example
 * <Flex>
 *   <FlexItem grow="1">
 *     <Icon name="checkmark" />
 *   </FlexItem>
 *   <FlexItem grow="2">
 *     <Button>Continue</Button>
 *   </FlexItem>
 * </Flex>
 */
export const FlexItem = styled('div', {
  shouldForwardProp: noneOf(['as', 'basis', 'grow', 'order', 'shrink']),
})`
  flex-basis: ${props => props.basis};
  flex-grow: ${props => props.grow};
  flex-shrink: ${props => props.shrink};
  order: ${props => props.order};
`

FlexItem.propTypes = {
  basis: PropTypes.string,
  children: PropTypes.node,
  grow: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  order: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  shrink: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
}
